import {
	SenderSource,
	WithClientID,
	IChatMessagesOut,
	isEventChannelOpenMessage,
} from '@enuan/chat__utils';
import { useEnuanLocalStorage } from '../../hooks/useEnuanLocalStorage';
import { IClientMessage } from '../../interfaces/IClientMessage';
import { RefObject, useCallback, useContext, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHistoryExhausted } from '../../reduxStore/selectors/messages.selector';
import { messagesSlice } from '../../reduxStore/slices/messages.slice';
import { StoredKey } from '../../utils/enuanLocalStorage';
import { ClientMessage } from '../../utils/ClientMessage';
import { v4 as uuidV4 } from 'uuid';
import { instanceContext } from 'src/contexts/instance/context';
import { configurationContext } from 'src/contexts/configuration/context';
import { configContext } from 'src/contexts/config/context';
import { ChannelState } from '../../interfaces/ChannelState';
import { messageContext } from 'src/components/message/message.context';
import classNames from 'classnames';

export const useAddReceivedMessage = () => {
	const dispatch = useDispatch();
	const addMessage = useCallback(
		(message: IClientMessage) => {
			return dispatch(messagesSlice.actions.addReceivedMessage(message))
				.payload;
		},
		[dispatch],
	);

	return addMessage;
};

/**
 * Waits for the channel to be open before resolving.
 * Polls at `intervalMs` intervals up to `maxRetries` times,
 * then rejects if the channel never opens.
 */
const waitForChannel = (
	channelStateRef: RefObject<ChannelState | undefined>,
	maxRetries: number,
	intervalMs: number,
): Promise<void> =>
	new Promise((resolve, reject) => {
		const attempt = (remaining: number) => {
			if (channelStateRef.current === ChannelState.CHANNEL_OPEN) {
				return resolve();
			}
			if (remaining <= 0) {
				return reject(new Error('sendMessage - Max retry'));
			}
			window.setTimeout(() => attempt(remaining - 1), intervalMs);
		};
		attempt(maxRetries);
	});

/**
 * Returns a function that sends a chat message through the provider.
 *
 * When `force` is false and the channel is not yet open, the send is
 * deferred using a polling retry (see {@link waitForChannel}).
 * On success the storage timestamp is updated; on failure the message
 * is marked as not sent in the store.
 *
 * @returns `(message, skipSave?) => Promise<string>` resolving to the `client_id`.
 */
export const useSendMessage = () => {
	const dispatch = useDispatch();
	const { instance: provider } = useContext(instanceContext);
	const { reconnection_retries = 5, reconnection_timeout = 10000 } =
		useContext(configurationContext);
	const { channelState } = useContext(configContext);
	const { updateStorageTimestamp } = useEnuanLocalStorage();

	const channelStateRef = useRef(channelState);
	channelStateRef.current = channelState;

	const send = useCallback(
		async <T extends WithClientID<IChatMessagesOut>>(
			message: T,
			force: boolean,
		): Promise<void> => {
			if (!provider) {
				throw new Error('sendMessage - Missing chatProvider');
			}
			if (!force) {
				await waitForChannel(
					channelStateRef,
					reconnection_retries,
					reconnection_timeout,
				);
			}
			return provider.sendMessage(message);
		},
		[provider, reconnection_retries, reconnection_timeout],
	);

	return useCallback(
		<T extends IChatMessagesOut>(message: T, skipSave?: boolean) => {
			const client_id = uuidV4();
			const outMessage: IChatMessagesOut &
				Pick<IClientMessage, 'client_id' | 'sender'> = {
				client_id,
				sender: SenderSource.USER,
				...message,
			};

			if (!skipSave) {
				const clientMessage = ClientMessage(outMessage, {
					sent: new Date().toISOString(),
				});
				dispatch(
					messagesSlice.actions.addSentMessage(clientMessage as IClientMessage),
				);
			}

			const force = isEventChannelOpenMessage(outMessage);

			return send(outMessage, force)
				.then(() => {
					updateStorageTimestamp(new Date().toISOString());
					return client_id;
				})
				.catch((error) => {
					if (!skipSave) {
						dispatch(
							messagesSlice.actions.setMeta({
								client_id,
								_meta: { sent: false },
							}),
						);
					}
					throw error;
				});
		},
		[dispatch, send, updateStorageTimestamp],
	);
};

/**
 * Returns a stable async function that loads an older page of messages from
 * the server and dispatches `setHistory` with the results.
 *
 * The guard against re-fetching when there are no more messages is kept in
 * redux (`historyExhausted`) rather than in a component-local ref so that
 * the flag can be reset from outside (e.g. after a trim) without requiring
 * the component to unmount and remount.
 *
 * The selector value is mirrored into a ref so the callback can read the
 * latest value without being recreated on every selector change.
 *
 * @returns Async function `(tstamp?, limit?) => Promise<IClientMessage[]>`.
 */
export const useGetHistory = () => {
	const { instance: provider } = useContext(instanceContext);
	const { get } = useEnuanLocalStorage();
	const dispatch = useDispatch();

	// Mirror the redux flag into a ref so the callback reads the latest value
	// without needing it as a dependency (which would destabilise useLoadHistory).
	const historyExhausted = useSelector(getHistoryExhausted);
	const historyExhaustedRef = useRef(historyExhausted);
	historyExhaustedRef.current = historyExhausted;

	const getHistory = useCallback(
		(tstamp?: string, limit?: number) => {
			if (historyExhaustedRef.current) return Promise.resolve([]);
			return provider?.getHistory(tstamp, limit).then((messages) => {
				if (messages.length === 0) {
					// Persist the exhausted state in redux so it survives re-renders and
					// can be explicitly reset (e.g. after trimOldMessages).
					dispatch(messagesSlice.actions.setHistoryExhausted(true));
					return [];
				}
				const { timestamp: newestUserMessageTimestamp = '' } =
					messages.find((message) => message.sender === SenderSource.USER) ??
					{};

				const lastReadMessageTimestamp =
					get(StoredKey.LAST_MESSAGE_TIMESTAMP) ?? '';

				const timestamp =
					lastReadMessageTimestamp || newestUserMessageTimestamp;

				const _messages = messages.map<IClientMessage>((message) =>
					ClientMessage(message, {
						sent: true,
						read: message.timestamp! <= timestamp,
					}),
				);

				dispatch(messagesSlice.actions.setHistory(_messages));
				return _messages;
			});
		},
		[dispatch, provider, get],
	);

	return getHistory;
};

export const useBlockMessageClassNames = () => {
	const { isLastMessage, isFirstMessage, isFirstBlock, isLastBlock } =
		useContext(messageContext);

	const className = useMemo(
		() =>
			classNames(
				isFirstMessage && 'message--first',
				isLastMessage && 'message--last',
				isLastBlock && isLastMessage && 'message--newest',
				isFirstBlock && isFirstMessage && 'message--oldest',
			),
		[isFirstMessage, isLastMessage, isFirstBlock, isLastBlock],
	);

	return className;
};
